import { resolveProductImageUrl } from '@/scripts/utils/productImage.utils';
import { createDomElementHandler, createSvg } from "@/scripts/utils/dom.utils";
import "./abPc1.style.scss";
import { ProductSlimResponseDTO } from "@shared/DTO";
import { PRODUCT_TARGET_VALUES } from "@shared/values";
import * as functionsHtml from "./abPc1.html";
import * as functions from "./abPc1.functions";
import { debounce } from "@/scripts/utils/debounce.util";
import { grammarFor } from "@shared/utils/grammar.utils";
import { createStandaloneProductCardViewModel } from "@/scripts/components/ab-products/variants/productListingItem";
import {
  ProductCardViewModel,
  PurchaseCtaModel,
} from "@/scripts/components/ab-products/variants/purchaseCta.types";

import { PurchaseScope } from "@/scripts/components/purchase-cta/purchaseCta.model";
import { PurchaseCtaView } from "@/scripts/components/purchase-cta/purchaseCta.view";

export class AbPc1Element extends HTMLElement {      

    #initialized = false;
    #resizeObserver: ResizeObserver;    
    #product: ProductSlimResponseDTO;    
    #purchaseCta: PurchaseCtaModel;
    #cardEl: HTMLElement;
    #purchaseScope?: PurchaseScope;
    #sectionLink?: string;
    #purchaseCtaView?: PurchaseCtaView;

    get #productName() {
      return this.#product?.productData?.names?.[0];
    }

    static get observedAttributes(): string[] {
      return ["data-layout", "data-view"];
    }

    connectedCallback() {
      this.#setLayoutClasses();
      this.bindEvents();
    }

    disconnectedCallback() {
      this.unbindEvents();
    }

    attributeChangedCallback(attr: string) {
      if (!this.isConnected) return;
      if(["data-layout", "data-view"].includes(attr)) {
        this.#setLayoutClasses();
      }
    }

    get product(): ProductSlimResponseDTO {
      return this.#product;
    }

    /**
     * Compatibility input for existing consumers. New listing renderers should
     * pass cardModel so that variant projection stays outside the card.
     */
    set product(value: ProductSlimResponseDTO) {
      this.cardModel = createStandaloneProductCardViewModel(value);
    }

    get cardModel(): ProductCardViewModel {
      return {
        product: this.#product,
        ...(this.#sectionLink ? { sectionLink: this.#sectionLink } : {}),
        purchaseCta: this.#purchaseCta,
        ...(this.#purchaseScope ? { purchaseScope: this.#purchaseScope } : {}),
      };
    }

    set cardModel(value: ProductCardViewModel) {
      this.#product = value.product;
      this.#sectionLink = value.sectionLink;
      this.#purchaseCta = value.purchaseCta;
      this.#purchaseScope = value.purchaseScope;
      this.#createBaseDOM();      
      this.#setRecommendedClass();
      this.#setImageEl();
      this.#setLayoutClasses();
      this.#setBadgeTargetEl();
      this.#setBadgeRecommendedEl();
      this.#setTitle();
      this.#setSubtitle();
      this.#setRatingEl();
      this.#setSpecsEl();
      this.#setUsageEl();
      this.#setCtaSpecsEl();
      this.#setCtaAffEl();
    }
    
    #createBaseDOM() {
      if (this.#initialized) return;
      this.#initialized = true;      
      this.#cardEl = createDomElementHandler("div", ["ab-pc-1"]);
      
      // badges
      const badgeTarget = createDomElementHandler("div", ["badge-target"]);
      const badgeRecommended = createDomElementHandler("div", ["badge-recommended"]);
      
      // Main body
      const imageWrapper = createDomElementHandler("div", ["ab-pc-1__section", "ab-pc-1__section--image"]);          
      const cardTitle = createDomElementHandler("h3", "ab-pc-1__title");
      const cardSubtitle = createDomElementHandler("em", "ab-pc-1__subtitle");
      const cardRating = createDomElementHandler("div", "ab-pc-1__rating");
      const cardSpecs = createDomElementHandler("ul", "ab-pc-1__spec-elements");
      const cardMain = createDomElementHandler("div", ["ab-pc-1__section", "ab-pc-1__section--main"]);
      cardMain.replaceChildren(cardTitle, cardSubtitle, cardRating, cardSpecs);
      const cardBody = createDomElementHandler("div", ["ab-pc-1__section", "ab-pc-1__section--body"]);
      cardBody.replaceChildren(imageWrapper, cardMain);
      
      // Usages
      const usages = createDomElementHandler("div", ["ab-pc-1__section", "ab-pc-1__section--usage"]);
      
      // CTAS
      const ctasEl = createDomElementHandler("div", ["ab-pc-1__section", "ab-pc-1__section--ctas"]);
      const ctaSpecsWrapper  = createDomElementHandler("div", ["ab-pc-1__section", "ab-pc-1__section--cta"]);
      const ctaAffWrapper = createDomElementHandler("div", ["ab-pc-1__section", "ab-pc-1__section--cta"]);
      ctasEl.replaceChildren(ctaSpecsWrapper, ctaAffWrapper)

      this.#cardEl.replaceChildren(badgeTarget, badgeRecommended, cardBody, usages, ctasEl);
      this.replaceChildren(this.#cardEl);
    }

    #setLayoutClasses() {
      
      const rootClasses: string[] = [];
      if(this.getAttribute("data-layout") === "horizontal") rootClasses.push("ab-pc-1--horizontal");      
      if(this.getAttribute("data-view") === 'compact') rootClasses.push("ab-pc-1--compact");

      ["ab-pc-1--compact", "ab-pc-1--horizontal"]
        .forEach( layoutClass => {
          if(rootClasses?.includes(layoutClass)) {
            this.#cardEl.classList.add(layoutClass);
            return;
          }
          this.#cardEl.classList.remove(layoutClass);
        });
    }

    #setRecommendedClass() {
      if(this.#product.productData.recommended) {
        this.#cardEl.classList.add("ab-pc-1--recommended");
      }
    }

    #setBadgeTargetEl() {
      const target = this.#product.productData?.target;
      const badgeTargetEl = this.querySelector(`.badge-target`);
      if(!target || !badgeTargetEl) return;
      const label = PRODUCT_TARGET_VALUES?.at(target - 1);
      const indicatorsCount = PRODUCT_TARGET_VALUES.length;      
      badgeTargetEl.innerHTML = functionsHtml.getBadgeTargetHtml(label, target, indicatorsCount);
    }

    #setBadgeRecommendedEl() {
      const badgeRecommended = this.querySelector(`.badge-recommended`);
      if (!badgeRecommended) return;
      const label = grammarFor(this.#product).adjective('Consigliato', 'Consigliata');
      badgeRecommended.innerHTML = functionsHtml.getBadgeRecommendedHtml(label);
    }

    #setImageEl() {
      const imgUrl = resolveProductImageUrl(this.#product.productData.images?.[0]?.image?.sizes?.[0]?.url);
      if (!imgUrl) { this.querySelector(`.ab-pc-1__section--image`)?.replaceChildren(); return; }
      const image = createDomElementHandler("img", "ab-pc-1__image", {src: imgUrl, alt: this.#productName});
      this.querySelector(`.ab-pc-1__section--image`)?.replaceChildren(image);
    }

    #setTitle() {
      this.querySelector(".ab-pc-1__title").textContent = this.#productName;      
    }
    
    #setSubtitle() {      
      this.querySelector(".ab-pc-1__subtitle").textContent = functions.getProductSubcategory(this.#product);
    }

    #setRatingEl() {
      const ratingStats = functions.getProductRating(this.#product);
      if(!ratingStats?.ratingAvg) return;
      this.querySelector(".ab-pc-1__rating")?.replaceChildren(...functionsHtml.getRatingHtml(ratingStats.ratingAvg, ratingStats.ratingCount));
    }

    #setSpecsEl() {

      const items = functions
        .getSpecsItems(this.#product)
        .map(({icon, label}) => {                
          const iconEl = createSvg(icon);
          const textEl = createDomElementHandler("span", "ab-pc-1__spec-element-text", undefined, label);        
          const itemEl = createDomElementHandler("li", "ab-pc-1__spec-element", undefined);
          itemEl.replaceChildren(iconEl, textEl);
          return itemEl;
        });

      const itemsListEl = createDomElementHandler("ul", "ab-pc-1__spec-elements");
      itemsListEl.replaceChildren(...items);
      this.querySelector(".ab-pc-1__spec-elements")?.replaceWith(itemsListEl);
    }

    #setUsageEl() {

      const usageItemsEls = functions
        .getBestUsages(this.#product)
        .map((name) => 
          createDomElementHandler("li", ["ab-pc-1__usage-item", `ab-pc-1__usage-item--${name}`], undefined, name)
      );

      const usageListEl = createDomElementHandler("ul", "ab-pc-1__usage-list");
      usageListEl.replaceChildren(...usageItemsEls);
      
      const scrollerEl = createDomElementHandler("ab-pretty-scroller");
      scrollerEl.replaceChildren(usageListEl);
      this.querySelector(`.ab-pc-1__section--usage`)?.replaceChildren(scrollerEl);
    }

    #setCtaSpecsEl() {
      const ctaSpecsWrapper = this.querySelectorAll(`.ab-pc-1__section--cta`)?.[0];
      const cta_specs = this.#sectionLink
        ? createDomElementHandler("a", ["ab-pc-1__cta", "ab-pc-1__cta--specs", "smooth-scroll"], { href: this.#sectionLink }, "Leggi nella guida")
        : functionsHtml.getCtaSpecsHtml(this.#product._id, this.#purchaseScope);
      ctaSpecsWrapper?.replaceChildren(cta_specs);
    }

    #setCtaAffEl() {
      const ctaAffWrapper = this.querySelectorAll(`.ab-pc-1__section--cta`)?.[1];
      this.#purchaseCtaView?.disconnect();
      this.#purchaseCtaView = new PurchaseCtaView(this.#purchaseCta, {
        cardClasses: true,
        onOpenChange: (open) => this.#cardEl.classList.toggle('ab-pc-1--affiliate-menu-open', open),
      });
      ctaAffWrapper?.replaceChildren(this.#purchaseCtaView.element);
      if (this.isConnected) this.#purchaseCtaView.connect();
    }

      private bindEvents() {                            
        const usagesSection = this.querySelector(`.ab-pc-1__section--usage`);
        this.#resizeObserver = new ResizeObserver(entries => {
          const entry = entries.find( e => e.target === usagesSection);
          this.#updateUsageElementsDebounced(entry?.contentRect?.width);
        });
        this.#resizeObserver.observe(usagesSection);
        this.#purchaseCtaView?.connect();
      }
    
      private unbindEvents() {
        this.#resizeObserver.disconnect();
        this.#resizeObserver = null;
        this.#purchaseCtaView?.disconnect();
      }
    
      #updateUsageElementsDebounced = debounce(this.updateUsageElements.bind(this), 500);

      private updateUsageElements(containerWidth: number) {      
        const gap = 8;
        const elementsPerView = 
          containerWidth <= 350 ? 2.5 :
          containerWidth <= 400 ? 2.75 :
          containerWidth <= 450 ? 3.25 :
          4;
        const widthSingle = (containerWidth + gap) / elementsPerView - gap;        
        this.querySelectorAll(`.ab-pc-1__usage-item`).forEach((el)=>{
          (el as HTMLElement).style.width = `${widthSingle}px`;
        })

      }

}


export function defineAbPc1Element() {
  const TAG_NAME = "ab-pc-1";
  if (customElements.get(TAG_NAME)) return;
  customElements.define(TAG_NAME, AbPc1Element);
}
